import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import type { AxiosError } from "axios";
import { getAllSchoolLogsService } from "@/service/school-log-service-provider";
import { SchoolLogModel, SchoolLogQuery } from "@/types/school-log.model";

// Re-export types from the types folder
export type { SchoolLogModel, SchoolLogQuery } from "@/types/school-log.model";

// State interface
interface SchoolLogState {
  schoolLogs: SchoolLogModel[];
  currentSchoolLog: SchoolLogModel | null;
  loading: boolean;
  error: string | null;
  totalItems: number;
  noSchoolLogs: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

// Initial state
const initialState: SchoolLogState = {
  schoolLogs: [],
  currentSchoolLog: null,
  loading: false,
  error: null,
  totalItems: 0,
  noSchoolLogs: true,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async thunks
export const fetchSchoolLogs = createAsyncThunk(
  "schoolLog/fetchAll",
  async (query: SchoolLogQuery, { dispatch }) => {
    try {
      dispatch(setLoading(true));
      const {
        data: { data, pageInfo },
      } = await getAllSchoolLogsService(query);
      dispatch(setLoading(false));

      dispatch(setNoSchoolLogs(false));

      return {
        schoolLogs: data,
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoSchoolLogs(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch school logs";
      dispatch(setError(message));
      throw error;
    }
  },
  {
    condition: (query, { getState }) => {
      const state = getState() as RootState;
      // Only allow this action to be executed if we're not already loading
      // This will cancel any pending requests when a new one is dispatched
      return !state.studentLog.loading;
    },
    // This will automatically cancel any pending thunk with the same action type
    dispatchConditionRejection: true,
  }
);

// School Log slice
const schoolLogSlice = createSlice({
  name: "schoolLog",
  initialState,
  reducers: {
    setNoSchoolLogs: (state, action: PayloadAction<boolean>) => {
      state.noSchoolLogs = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentSchoolLog: (state) => {
      state.currentSchoolLog = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchoolLogs.fulfilled, (state, action) => {
        state.schoolLogs = action.payload.schoolLogs;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchSchoolLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch school logs";
      });
  },
});

// Export actions
export const {
  setNoSchoolLogs,
  setLoading,
  setError,
  clearError,
  resetCurrentSchoolLog,
  resetAllState,
} = schoolLogSlice.actions;

// Selectors
export const selectSchoolLogs = (state: RootState) =>
  state.schoolLog.schoolLogs;
export const selectCurrentSchoolLog = (state: RootState) =>
  state.schoolLog.currentSchoolLog;
export const selectSchoolLogLoading = (state: RootState) =>
  state.schoolLog.loading;
export const selectSchoolLogError = (state: RootState) => state.schoolLog.error;
export const selectTotalItems = (state: RootState) =>
  state.schoolLog.pagination.totalItems;
export const selectNoSchoolLogs = (state: RootState) =>
  state.schoolLog.noSchoolLogs;

export default schoolLogSlice.reducer;
